import { Injectable, computed, effect, inject, signal } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, tap } from 'rxjs';
import {
  MonthLock,
  MonthLockScope,
  MonthUnlockDto,
  RedLockOverviewDto,
  isDayLocked,
  refusesEdits,
} from '@anessa/shared-models';
import { ProjectService } from '../services/project/project.service';
import { MyAuthService } from '../../auth/auth.service';
import { isoDay } from '../date/app-date-format';

/**
 * The current plant's month lock (see `monthLock` in shared-models): pages
 * that write dated data disable their inputs for a locked day. The server
 * refuses those writes regardless; this only explains it up front.
 *
 * Also tracks the escape-hatch unlock the caller is on (`~unlock`, opened by
 * Anessa staff, who may add others with `~unlockadd`): a day in that month of
 * that plant counts as unlocked here, exactly as the server lets their writes
 * through.
 */
@Injectable({ providedIn: 'root' })
export class MonthLockService {
  private readonly http = inject(HttpClient);
  private readonly projectService = inject(ProjectService);
  private readonly authService = inject(MyAuthService);

  private readonly loaded = signal<{ plantId: string; lock: MonthLock } | null>(null);

  /** The current plant's lock; null until loaded or without a single plant. */
  readonly lock = computed<MonthLock | null>(() => {
    const plantId = this.projectService.singlePlantId;
    const loaded = this.loaded();
    return plantId && loaded?.plantId === plantId ? loaded.lock : null;
  });

  /** The open unlock the caller holds or was added to, on any plant; null when none. */
  readonly myUnlock = signal<MonthUnlockDto | null>(null);

  /**
   * "Now" as of the last {@link refresh}: lets date-window computeds (e.g. the
   * RED edit window) move on when a tab stays open across a lock day, instead
   * of reading a `new Date()` they never re-evaluate.
   */
  readonly now = signal(new Date());

  constructor() {
    effect(() => {
      const plantId = this.projectService.singlePlantId;
      if (plantId) this.load(plantId);
    });
    // Anyone may be on an unlock: its staff holder, or a person they added
    // (`~unlockadd`, e.g. a client account), so every signed-in user asks.
    // Layout re-asks periodically, since someone can be added (or the unlock
    // relocked) while their app is open.
    effect(() => {
      if (this.authService.ensuredUser()) this.refreshMyUnlock();
    });
  }

  /**
   * Whether this user's `scope` write dated `day` (`YYYY-MM-DD` or a Date) is
   * locked for the current plant: never while that kind of write isn't refused
   * yet (`RED_RULES.editLockFrom`, ADEV-1251), the server letting it through too.
   */
  isLocked(day: string | Date | null | undefined, scope: MonthLockScope): boolean {
    const lock = this.lock();
    if (!day || !lock || !refusesEdits(scope, this.now())) return false;
    const iso = typeof day === 'string' ? day.slice(0, 10) : isoDay(day);
    if (!isDayLocked(iso, lock)) return false;
    const unlock = this.myUnlock();
    const unlocked =
      !!unlock &&
      unlock.plantId === this.projectService.singlePlantId &&
      iso.slice(0, 7) === unlock.month &&
      Date.parse(unlock.expiresAt) > Date.now();
    return !unlocked;
  }

  /** `~unlock`: opens an escape-hatch unlock of `month` on the current plant. */
  openUnlock(plantId: string, month: string, reason: string): Observable<MonthUnlockDto> {
    return this.http
      .post<MonthUnlockDto>('/api/month-lock/unlocks', { plantId, month, reason })
      .pipe(tap((unlock) => this.myUnlock.set(unlock)));
  }

  /** `~unlockadd`: adds someone to the caller's open unlock (RED_RULES.unlockMaxDelegates). */
  addDelegate(email: string): Observable<MonthUnlockDto> {
    return this.http
      .post<MonthUnlockDto>('/api/month-lock/unlocks/delegates', { email })
      .pipe(tap((unlock) => this.myUnlock.set(unlock)));
  }

  /** `~relock`: closes the unlock the caller is on, for everyone on it; null when none was open. */
  relock(): Observable<MonthUnlockDto | null> {
    return this.http
      .post<MonthUnlockDto | null>('/api/month-lock/unlocks/relock', {})
      .pipe(tap(() => this.myUnlock.set(null)));
  }

  /** The plant's open unlocks and RED month versions — the `~lockstatus` dialog's server-side half (Anessa staff). */
  overview(plantId: string): Observable<RedLockOverviewDto> {
    return this.http.get<RedLockOverviewDto>('/api/red/lock-overview', {
      params: new HttpParams().set('plantId', plantId),
    });
  }

  /** Forgets an unlock whose time is up (the server already ignores it). */
  clearExpiredUnlock(): void {
    const unlock = this.myUnlock();
    if (unlock && Date.parse(unlock.expiresAt) <= Date.now()) this.myUnlock.set(null);
  }

  /**
   * Re-reads the current plant's lock and the caller's unlock, and moves `now`
   * on — Layout calls it every minute and when the tab comes back into view,
   * so a page left open across a lock day locks without a reload.
   */
  refresh(): void {
    this.now.set(new Date());
    const plantId = this.projectService.singlePlantId;
    if (plantId) this.load(plantId);
    this.refreshMyUnlock();
  }

  private load(plantId: string): void {
    this.http
      .get<MonthLock>('/api/month-lock', { params: new HttpParams().set('plantId', plantId) })
      .subscribe({
        // A late answer for a plant switched away from must not replace the
        // current plant's lock (it would show as unlocked until a reload).
        next: (lock) => {
          if (this.projectService.singlePlantId === plantId) this.loaded.set({ plantId, lock });
        },
        // Reported by api-error.interceptor; without a lock the pages stay
        // editable and the server still refuses locked writes.
        error: () => undefined,
      });
  }

  /** Re-reads the open unlock the caller is on, if any. */
  refreshMyUnlock(): void {
    this.http.get<MonthUnlockDto | null>('/api/month-lock/unlocks/mine').subscribe({
      next: (unlock) => this.myUnlock.set(unlock),
      // Reported by api-error.interceptor; without it the pages stay locked.
      error: () => undefined,
    });
  }
}
